import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { isAppInFront, onAppForeground } from '@/lib/app-presence.js';

import type {
  DriveFileItem,
  DriveFileVersionList,
} from '@/lib/api/drive-files.js';
import type { FileReference } from '@/lib/api/file-chat.js';
import {
  downloadOneDriveFileVersion,
  getOneDriveItem,
  listOneDriveFileVersions,
  restoreOneDriveFileVersion,
} from '@/lib/api/onedrive.js';
import {
  fetchSharePointFileVersionBlob,
  getSharePointFileItem,
  listSharePointFileVersions,
  restoreSharePointFileVersion,
} from '@/lib/api/sharepoint.js';

/** How often an open file's eTag is read again, so an edit made elsewhere shows up. */
export const LIVE_FILE_POLL_MS = 4_000;

/**
 * The live poll's interval: every few seconds while the app is in front of the user, and nothing while
 * the page is hidden or Teams keeps the tab suspended. {@link useRefetchOnForeground} restarts it.
 */
export function livePollInterval(): number | false {
  return isAppInFront() ? LIVE_FILE_POLL_MS : false;
}

/** Reads a live query again when the app comes back to the user, which also restarts its poll. */
export function useRefetchOnForeground(
  queryKey: readonly unknown[],
  enabled: boolean,
): void {
  const queryClient = useQueryClient();
  const keyText = JSON.stringify(queryKey);
  useEffect(() => {
    if (!enabled) return undefined;
    return onAppForeground(() => {
      void queryClient.refetchQueries({
        queryKey: JSON.parse(keyText) as unknown[],
        exact: true,
      });
    });
  }, [queryClient, keyText, enabled]);
}

/**
 * Where a SharePoint or OneDrive file lives, as the viewer addresses it. A SharePoint file is read
 * through the connection it was found in when one is named; a OneDrive file through the caller's own
 * folder scope.
 */
export type ExternalFileLocation =
  | {
      source: 'sharepoint';
      connectionId: string | null;
      driveId: string;
      itemId: string;
    }
  | { source: 'onedrive'; scopeId: string; itemId: string };

/** The file as the Assistant's thread and the agent address it. */
export function externalFileReference(
  location: ExternalFileLocation,
): FileReference {
  return location.source === 'sharepoint'
    ? {
        source: 'sharepoint',
        driveId: location.driveId,
        itemId: location.itemId,
      }
    : {
        source: 'onedrive',
        scopeId: location.scopeId,
        itemId: location.itemId,
      };
}

/**
 * One string per file, for cache keys. A SharePoint key names the connection too: Graph ids repeat
 * between tenants, so the same drive and item ids in another connection are another file.
 */
export function externalLocationKey(location: ExternalFileLocation): string {
  return location.source === 'sharepoint'
    ? `sp:${location.connectionId ?? ''}:${location.driveId}:${location.itemId}`
    : `od:${location.scopeId}:${location.itemId}`;
}

/** The file's current metadata, its eTag above all. */
export async function loadExternalItem(
  workspaceId: string,
  location: ExternalFileLocation,
): Promise<Pick<DriveFileItem, 'etag'>> {
  if (location.source === 'sharepoint') {
    return getSharePointFileItem(
      workspaceId,
      location.connectionId,
      location.driveId,
      location.itemId,
    );
  }
  return getOneDriveItem(location.scopeId, location.itemId, workspaceId);
}

/** The file's stored versions, newest first. */
export function listExternalVersions(
  workspaceId: string,
  location: ExternalFileLocation,
): Promise<DriveFileVersionList> {
  return location.source === 'sharepoint'
    ? listSharePointFileVersions(
        workspaceId,
        location.connectionId,
        location.driveId,
        location.itemId,
      )
    : listOneDriveFileVersions(location.scopeId, location.itemId, workspaceId);
}

/** The bytes of one stored version. */
export function loadExternalVersion(
  workspaceId: string,
  location: ExternalFileLocation,
  versionId: string,
): Promise<Blob> {
  return location.source === 'sharepoint'
    ? fetchSharePointFileVersionBlob(
        workspaceId,
        location.connectionId,
        location.driveId,
        location.itemId,
        versionId,
      )
    : downloadOneDriveFileVersion(
        location.scopeId,
        location.itemId,
        versionId,
        workspaceId,
      );
}

/** Makes an older version current; `ifMatch` is the eTag last read, so a changed file is refused. */
export async function restoreExternalVersion(
  workspaceId: string,
  location: ExternalFileLocation,
  versionId: string,
  ifMatch: string,
): Promise<void> {
  if (location.source === 'sharepoint') {
    await restoreSharePointFileVersion({
      workspaceId,
      connectionId: location.connectionId,
      driveId: location.driveId,
      itemId: location.itemId,
      versionId,
      ifMatch,
    });
    return;
  }
  await restoreOneDriveFileVersion({
    scopeId: location.scopeId,
    itemId: location.itemId,
    versionId,
    workspaceId,
    ifMatch,
  });
}

export const externalFileKeys = {
  item: (workspaceId: string, location: ExternalFileLocation) =>
    ['external-file', workspaceId, externalLocationKey(location)] as const,
  versions: (workspaceId: string, location: ExternalFileLocation) =>
    [
      'external-file',
      workspaceId,
      externalLocationKey(location),
      'versions',
    ] as const,
};

/**
 * The open file's eTag, read again every few seconds while the tab is visible, so an edit made by the
 * Assistant or by anyone else shows up. Null until the first read answers. A change after that first
 * read means a new version exists, so the version history is read again too. `settled` is true once
 * the first read answered either way, or at once when there is nothing to read.
 */
export function useLiveExternalEtag(
  workspaceId: string,
  location: ExternalFileLocation | undefined,
): { etag: string | null; settled: boolean } {
  const queryKey = location
    ? externalFileKeys.item(workspaceId, location)
    : ['external-file', 'none'];
  const query = useQuery({
    queryKey,
    queryFn: () => loadExternalItem(workspaceId, location!),
    enabled: location !== undefined,
    refetchInterval: livePollInterval,
    retry: false,
  });
  useRefetchOnForeground(queryKey, location !== undefined);
  const etag = query.data?.etag ?? null;
  useVersionsFollowEtag(workspaceId, location, etag);
  return {
    etag,
    settled: location === undefined || query.isSuccess || query.isError,
  };
}

/** Reads the version history again whenever the eTag changes after the first one seen. */
function useVersionsFollowEtag(
  workspaceId: string,
  location: ExternalFileLocation | undefined,
  etag: string | null,
): void {
  const queryClient = useQueryClient();
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (!location || etag === null) return;
    const previous = seen.current;
    seen.current = etag;
    if (previous === null || previous === etag) return;
    void queryClient.invalidateQueries({
      queryKey: externalFileKeys.versions(workspaceId, location),
    });
  }, [queryClient, workspaceId, location, etag]);
}

/** Everything that tells two locations apart. */
function locationIdentity(location: ExternalFileLocation | undefined): string {
  return location ? externalLocationKey(location) : '';
}

/**
 * The same location object for as long as it names the same file, so a caller may build it inline
 * without making every hook below look the file up again.
 */
export function useStableLocation(
  location: ExternalFileLocation | undefined,
): ExternalFileLocation | undefined {
  const held = useRef(location);
  if (locationIdentity(held.current) !== locationIdentity(location)) {
    held.current = location;
  }
  return held.current;
}
